import { z } from "zod";
import { ProposedEncounterImagingOrderSchema } from "./agent.js";
import { DocumentRequestContextSchema } from "./clinic-documents.js";
import { LabOrderDraftDocumentSchema, PlacedOrderTestSchema, PreparedFormNoticesSchema, PreparedFormsIncompleteSchema, PreviewedLabFormSchema, } from "./lab-order-drafts.js";
import { PreparedLabOrderSchema } from "./lab-order-governance.js";
import { refineManualOrderRunKey } from "./manual-order-marker.js";
import { IdempotencyKeySchema, IdSchema } from "./primitives.js";
export const ImagingPreviewSourcesSchema = DocumentRequestContextSchema.pick({
    patient: true,
    encounter: true,
    provider: true,
    clinic: true,
});
export const PreviewImagingOrderRequestSchema = z
    .object({
    proposedImagingOrder: ProposedEncounterImagingOrderSchema,
    manualValues: z.record(z.string().min(1).max(200), z.string().max(10_000)).default({}),
    /**
     * The form the manual values were typed on. The preview draws the studies on the form whose
     * reviewed boxes carry them, so values typed on another form are left out, never moved.
     */
    manualValuesDocumentId: IdSchema.optional(),
})
    .strict();
export const ImagingOrderDraftPreviewSchema = z
    .object({
    document: LabOrderDraftDocumentSchema,
    sources: ImagingPreviewSourcesSchema,
    pdfBase64: z.string().min(1),
    fieldValuesHash: z.string().regex(/^sha256:[0-9a-f]{64}$/),
    /**
     * The form pages the preview drew. With the printed values' hash they tell whether the forms
     * prepared for signing are exactly the forms on screen, so they need not replace them.
     */
    includedPages: z.array(z.number().int().positive()).min(1).max(200).optional(),
    mode: z.enum(["approved_execution", "draft_only"]).optional(),
    /** Non-blocking notes about the previewed form, such as a field left for the clinician. */
    notices: PreparedFormNoticesSchema.optional(),
})
    .strict();
export const PrepareImagingOrderRequestSchema = z
    .object({
    expectedEncounterVersion: z.number().int().positive(),
    proposedImagingOrder: ProposedEncounterImagingOrderSchema,
    previewedDocument: LabOrderDraftDocumentSchema.omit({ fields: true }).optional(),
    previewedSources: ImagingPreviewSourcesSchema.optional(),
    previewedFieldValuesHash: z
        .string()
        .regex(/^sha256:[0-9a-f]{64}$/)
        .optional(),
    manualValues: z.record(z.string().min(1).max(200), z.string().max(10_000)).default({}),
    idempotencyKey: IdempotencyKeySchema,
    /** Tests suggested by clinic instructions that the physician chose not to order. */
    declinedConceptIds: z.array(z.string().trim().min(1).max(160)).max(100).optional(),
    /**
     * Sent by a caller that lets the physician complete the forms by hand, empty at first: a study
     * no form field shows then comes back listed instead of refusing the preparation. Its entries
     * are the studies the physician confirmed they added to the forms by hand.
     */
    placedTests: z.array(PlacedOrderTestSchema).max(40).optional(),
    /**
     * The forms on screen, in order, as a laboratory preparation sends them. A prepared set that
     * is exactly these comes back without the editor fields, which the physician already has.
     */
    previewedForms: z.array(PreviewedLabFormSchema).max(10).optional(),
})
    .strict()
    .refine((request) => {
    const pins = [
        request.previewedDocument,
        request.previewedSources,
        request.previewedFieldValuesHash,
    ];
    return pins.every((value) => value === undefined) || pins.every((value) => value !== undefined);
}, "Legacy imaging preview bindings must be supplied together")
    // A "+" imaging order runs only under its own key, and its key only with its marker.
    .superRefine((request, context) => refineManualOrderRunKey(request.proposedImagingOrder.manualOrder, request.idempotencyKey, context));
// The mode-bound review/selection/artifact envelope is shared by diagnostic orders.
export const PreparedImagingOrderSchema = PreparedLabOrderSchema.extend({
    documentNames: z.record(z.string(), z.string()).optional(),
    documents: z
        .array(LabOrderDraftDocumentSchema.extend({
        includedPages: z.array(z.number().int().positive()).min(1),
    }))
        .optional(),
    /** Non-blocking notes about the prepared forms, such as a field left for the clinician. */
    notices: PreparedFormNoticesSchema.optional(),
    /** Required information still missing; the forms can be completed but not signed yet. */
    incomplete: PreparedFormsIncompleteSchema.optional(),
    /**
     * Answers `previewedForms`: true when the prepared forms are exactly the forms on screen, so
     * their field sets are not sent. Absent when the request sent none.
     */
    previewMatches: z.boolean().optional(),
});
/**
 * What preparing a "+" lab or imaging order answers: the diagnostic-order envelope above, with
 * the prepared forms' notices, the information still missing on them (instead of a refusal), their
 * editor fields when they differ from the forms on screen, and whether they match those forms.
 * Declared beside its shape, because the lab request module cannot import this one.
 */
export const PreparedManualOrderSchema = PreparedImagingOrderSchema;
//# sourceMappingURL=imaging-order-governance.js.map